import React from 'react';

/**
 * Skeleton Component
 *
 * Premium loading placeholder with shimmer animation.
 * Creates delightful loading experiences that feel fast.
 */

// Shimmer styles injected once
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  .skeleton-shimmer {
    background: linear-gradient(
      90deg,
      #f1f5f9 0%,
      #f1f5f9 40%,
      #e2e8f0 50%,
      #f1f5f9 60%,
      #f1f5f9 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
  }

  .skeleton-shimmer-warm {
    background: linear-gradient(
      90deg,
      #fff5f5 0%,
      #fff5f5 40%,
      #ffe4e6 50%,
      #fff5f5 60%,
      #fff5f5 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
  }
`;

// Inject styles once
if (typeof document !== 'undefined' && !document.getElementById('skeleton-shimmer-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'skeleton-shimmer-styles';
  styleElement.textContent = shimmerStyles;
  document.head.appendChild(styleElement);
}

export function Skeleton({
  width,
  height,
  rounded = 'md',
  className = '',
  variant = 'default', // 'default' | 'warm'
  ...props
}) {
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    full: 'rounded-full',
  };

  const shimmerClass = variant === 'warm' ? 'skeleton-shimmer-warm' : 'skeleton-shimmer';

  return (
    <div
      className={`
        ${shimmerClass}
        ${roundedClasses[rounded] || roundedClasses.md}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      style={{ width, height }}
      {...props}
    />
  );
}

/**
 * SkeletonText - Text placeholder
 */
export function SkeletonText({
  lines = 1,
  className = '',
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height="1rem"
          width={i === lines - 1 ? '75%' : '100%'}
          rounded="sm"
        />
      ))}
    </div>
  );
}

/**
 * SkeletonAvatar - Avatar placeholder
 */
export function SkeletonAvatar({
  size = 'md',
  className = '',
  variant = 'default',
}) {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
  };

  return (
    <Skeleton
      rounded="full"
      className={`${sizes[size]} ${className}`}
      variant={variant}
    />
  );
}

/**
 * SkeletonCard - Card placeholder
 */
export function SkeletonCard({
  className = '',
  ...props
}) {
  return (
    <div
      className={`card p-5 ${className}`}
      {...props}
    >
      <div className="flex items-center gap-4">
        <SkeletonAvatar size="lg" />
        <div className="flex-1">
          <Skeleton height="1.25rem" width="60%" rounded="sm" />
          <Skeleton height="1rem" width="40%" rounded="sm" className="mt-2" />
        </div>
      </div>
      <div className="mt-4">
        <SkeletonText lines={2} />
      </div>
    </div>
  );
}

/**
 * SkeletonPersonCard - Person card placeholder
 */
export function SkeletonPersonCard({
  className = '',
}) {
  return (
    <div className={`person-card ${className}`}>
      <SkeletonAvatar size="lg" />
      <div className="flex-1">
        <Skeleton height="1.25rem" width="120px" rounded="sm" />
        <Skeleton height="0.875rem" width="80px" rounded="sm" className="mt-2" />
      </div>
      <Skeleton height="1.5rem" width="60px" rounded="full" />
    </div>
  );
}

/**
 * SkeletonTimelineCard - Timeline card placeholder
 * Premium version with beautiful gradient background
 */
export function SkeletonTimelineCard({
  className = '',
  variant = 'default',
}) {
  return (
    <div className={`bg-white rounded-2xl p-5 shadow-sm border border-slate-100 ${className}`}>
      <div className="flex items-start gap-4">
        <SkeletonAvatar size="lg" variant={variant} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <Skeleton height="1.25rem" width="140px" rounded="lg" variant={variant} />
            <Skeleton height="1.75rem" width="80px" rounded="full" variant={variant} />
          </div>
          <Skeleton height="1rem" width="100px" rounded="lg" className="mt-2" variant={variant} />
          <Skeleton height="0.875rem" width="180px" rounded="lg" className="mt-3" variant={variant} />
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100">
        <Skeleton height="2.5rem" width="100%" rounded="xl" variant={variant} />
      </div>
    </div>
  );
}

/**
 * SkeletonHeroCard - Large hero/featured card placeholder
 */
export function SkeletonHeroCard({
  className = '',
}) {
  return (
    <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 p-6 ${className}`}>
      <div className="flex items-start gap-4">
        <Skeleton width="64px" height="64px" rounded="2xl" variant="warm" />
        <div className="flex-1">
          <Skeleton height="0.75rem" width="80px" rounded="full" variant="warm" />
          <Skeleton height="1.5rem" width="200px" rounded="lg" className="mt-2" variant="warm" />
          <Skeleton height="1rem" width="150px" rounded="lg" className="mt-2" variant="warm" />
        </div>
      </div>
      <div className="mt-6">
        <Skeleton height="3rem" width="100%" rounded="xl" variant="warm" />
      </div>
    </div>
  );
}

/**
 * SkeletonDashboard - Full dashboard loading state
 */
export function SkeletonDashboard() {
  return (
    <div className="space-y-6 p-4">
      {/* Greeting skeleton */}
      <div className="space-y-2">
        <Skeleton height="2rem" width="200px" rounded="lg" />
        <Skeleton height="1rem" width="300px" rounded="lg" />
      </div>

      {/* Hero card skeleton */}
      <SkeletonHeroCard />

      {/* Section title */}
      <Skeleton height="1rem" width="120px" rounded="lg" className="mt-6" />

      {/* Timeline cards */}
      <div className="space-y-4">
        <SkeletonTimelineCard />
        <SkeletonTimelineCard />
      </div>

      {/* Section title */}
      <Skeleton height="1rem" width="100px" rounded="lg" className="mt-6" />

      {/* Recent gifts row */}
      <div className="flex gap-3 overflow-hidden">
        <Skeleton width="120px" height="80px" rounded="xl" className="flex-shrink-0" />
        <Skeleton width="120px" height="80px" rounded="xl" className="flex-shrink-0" />
        <Skeleton width="120px" height="80px" rounded="xl" className="flex-shrink-0" />
      </div>
    </div>
  );
}

/**
 * SkeletonList - List of skeleton items
 */
export function SkeletonList({
  count = 3,
  ItemComponent = SkeletonCard,
  className = '',
  itemClassName = '',
}) {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <ItemComponent key={i} className={itemClassName} />
      ))}
    </div>
  );
}

export default Skeleton;
